/**
 * Bestellliste v2 auf /plx-orders (Plan `.claude/plans/2026-10-20-bestellliste-v2.md`). Ersetzt dort die
 * generische `TableWidget` — die bleibt für die ~20 anderen Tabellen unverändert.
 *
 * Gebaut nach vier Persona-Durchgängen (Marketing, GF, Kundenservice, UX): sechs zweizeilige Spalten ohne
 * Querscrollen bei 1.280 px, lesbare Herkunft statt Roh-Kennung, ein Stand statt drei Chips, Suche über
 * E-Mail/Name/Bestell-/Kundennummer/Produkt/Kampagne/Webinar (im Zeitraum, auf Knopfdruck über alle
 * Zeiträume), Schnellfilter mit Zahl, 25/50/100/250/Alle Zeilen (gemerkt), Summen über die sichtbare
 * Auswahl, Excel wie angezeigt.
 *
 * Bewusst native `<table>`-Elemente mit EINEM Stilblock statt MUI-`TableCell`/`Chip` je Zelle: bei „Alle“
 * stehen über 3.000 Zeilen im DOM, und je Zelle eine Emotion-Klasse kostete dort Sekunden.
 */
import {
  FC,
  KeyboardEvent,
  memo,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { exportToExcel } from '../../helpers/exportFuncs';
import { formatEur, TERMS } from '../../helpers/kpiDefinitions';
import { formatNumber } from '../../helpers/numberUtils';
import { fetchPlxOrderList, PlxOrderListMeta, PlxOrderListRow } from '../../helpers/plxOrdersData';
import { tokens } from '../../theme';
import { useBreitePx } from '../../components/auswertungen/kennzahlTeile';
import {
  datumZeit,
  exportZeile,
  geld,
  herkunft,
  kundeMarken,
  kundeName,
  namensTreffer,
  nettoText,
  passtSuche,
  preisText,
  SCHNELLFILTER,
  SortKey,
  sortiere,
  stand,
  suchtext,
  summen,
} from './orderListModel';

/** Anker für den Sprung aus dem Seitenkopf. */
export const ORDER_LIST_ANCHOR = 'bestellungen';

const ZEILEN_OPTIONEN = [25, 50, 100, 250, { label: 'Alle', value: -1 }];
const ZEILEN_STANDARD = 100;
const ZEILEN_KEY = 'plxOrderList.rowsPerPage';

// localStorage kann in privaten Fenstern werfen — nur Komfort, nie Voraussetzung.
const ladeZeilen = (): number => {
  try {
    const v = Number(window.localStorage.getItem(ZEILEN_KEY));
    return [25, 50, 100, 250, -1].includes(v) ? v : ZEILEN_STANDARD;
  } catch {
    return ZEILEN_STANDARD;
  }
};
const merkeZeilen = (v: number) => {
  try {
    window.localStorage.setItem(ZEILEN_KEY, String(v));
  } catch {
    /* egal */
  }
};

/** Lokale Datumsbestandteile — `toISOString()` auf einem Datepicker-Wert verschiebt den Tag (CLAUDE.md). */
const ymd = (d: Date | null | undefined): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

interface Spalte {
  key: SortKey;
  label: string;
  tip: string;
  breite: string;
  align?: 'right';
}

const SPALTEN: Spalte[] = [
  {
    key: 'bestellt',
    label: 'Bestellt',
    tip: 'Bestellzeitpunkt (Berliner Zeit), darunter die Bestellnummer.',
    breite: '12%',
  },
  {
    key: 'produkt',
    label: 'Produkt',
    tip: 'Produkt, darunter Werbeaktion (WKZ) und Listenpreis brutto.',
    breite: '20%',
  },
  {
    key: 'herkunft',
    label: 'Herkunft',
    tip: 'Kanal der Bestellung, darunter das Webinar bzw. Direkt-Mailing — sonst die Kennung (Banner). Mit der Maus über der Zeile steht die volle Kennung.',
    breite: '23%',
  },
  {
    key: 'kunde',
    label: 'Kunde',
    tip: 'E-Mail und Name. Darunter Kundentyp und Auffälligkeiten (nur für Anfos mit bekanntem Zahlstand). Das Personen-Symbol sucht alle Bestellungen dieser Adresse über alle Zeiträume.',
    breite: '23%',
  },
  {
    key: 'stand',
    label: 'Stand',
    tip: 'Abo-Typ wie in den Kacheln oben, darunter Testphasen-Ende, Grund für „Unbezahlt“ und Vertragsstand laut PL-X.',
    breite: '13%',
  },
  { key: 'umsatz', label: 'Umsatz', tip: TERMS.umsatz.tip, breite: '9%', align: 'right' },
];

/**
 * Mit Netto-Spalte (Sektion /auswertungen/bestellungen, P5): „Bisher bezahlt (netto)" neben dem brutto-Umsatz, die
 * übrigen Spalten etwas schmaler. Ohne den Schalter bleibt die Liste auf /plx-orders unverändert.
 */
const SPALTEN_NETTO: Spalte[] = [
  { ...SPALTEN[0], breite: '11%' },
  { ...SPALTEN[1], breite: '17%' },
  { ...SPALTEN[2], breite: '20%' },
  { ...SPALTEN[3], breite: '20%' },
  { ...SPALTEN[4], breite: '12%' },
  { ...SPALTEN[5], label: 'Brutto', tip: `${TERMS.umsatz.tip} (Spalte „Umsatz“ der alten Liste.)`, breite: '10%' },
  {
    key: 'netto',
    label: 'Netto',
    tip: `${TERMS.bezahltNettoListe.tip} Vorläufig ergänzte Anfos tragen „${TERMS.vorlaeufig.short}“ und zählen nicht in die Summe oben.`,
    breite: '10%',
    align: 'right',
  },
];

/**
 * Unter dieser Breite der Liste Karten statt Tabelle: bei 660 px (1.024er Fenster mit Seitenleiste) war die Hälfte der
 * Zellen abgeschnitten, bei ~850 px (1.200er Fenster) passt die Tabelle noch.
 */
const KARTEN_UNTER_PX = 760;

/**
 * Sortierung der Kartenansicht: dieselben Schlüssel wie die Spaltenköpfe, je Richtung ein Eintrag — vollständig, damit
 * eine in der Tabelle gewählte Sortierung nach dem Umschalten auf Karten als Wert existiert.
 */
const kartenSortierung = (netto: boolean): { wert: string; label: string }[] => [
  { wert: 'bestellt:desc', label: 'Bestellt: neueste zuerst' },
  { wert: 'bestellt:asc', label: 'Bestellt: älteste zuerst' },
  { wert: 'produkt:asc', label: 'Produkt A–Z' },
  { wert: 'produkt:desc', label: 'Produkt Z–A' },
  { wert: 'herkunft:asc', label: 'Herkunft A–Z' },
  { wert: 'herkunft:desc', label: 'Herkunft Z–A' },
  { wert: 'kunde:asc', label: 'Kunde (E-Mail) A–Z' },
  { wert: 'kunde:desc', label: 'Kunde (E-Mail) Z–A' },
  { wert: 'stand:asc', label: 'Stand: Testphase zuerst' },
  { wert: 'stand:desc', label: 'Stand: umgekehrt' },
  { wert: 'umsatz:desc', label: `${netto ? 'Brutto' : 'Umsatz'}: höchster zuerst` },
  { wert: 'umsatz:asc', label: `${netto ? 'Brutto' : 'Umsatz'}: niedrigster zuerst` },
  ...(netto
    ? [
        { wert: 'netto:desc', label: 'Netto: höchster zuerst' },
        { wert: 'netto:asc', label: 'Netto: niedrigster zuerst' },
      ]
    : []),
];

interface ZeileProps {
  r: PlxOrderListRow;
  onOpen: (ordernumber: string) => void;
  onPerson: (email: string) => void;
  /** Netto-Spalte zeigen (P5). */
  netto?: boolean;
}

/** Vorläufig ergänzte Anfo: Betrag steht in der Zeile, zählt aber nicht in die Summe (wie die Übersichten). */
const nettoVorlaeufig = (r: PlxOrderListRow): boolean => r.is_anfo && r.is_provisional;

const Marken: FC<{ r: PlxOrderListRow; sektion?: boolean }> = ({ r, sektion }) => (
  <>
    {kundeMarken(r, sektion).map((m) => (
      <span key={m.label} className={`marke ${m.ton}`} title={m.tip}>
        {m.label}
      </span>
    ))}
  </>
);

const HerkunftZelle: FC<{ r: PlxOrderListRow; sektion?: boolean }> = ({ r, sektion }) => {
  const h = herkunft(r, sektion);
  return (
    <>
      <div className="eins">{h.kanal}</div>
      <div className="zwei" title={h.kennung ?? undefined}>
        {/* Sektion: der Blog wie in der Übersicht „Je Blog" vor dem Webinar/Mailing bzw. der Kennung. */}
        {h.blog && (
          <>
            {h.blog}
            {h.detail ? ' · ' : ''}
          </>
        )}
        {h.link ? (
          <Link
            component={RouterLink}
            to={h.link}
            title={h.linkTitel ?? undefined}
            underline="hover"
            // Nicht primary.main: das ist in diesem Theme fast schwarz (`.claude/rules/react-frontend.md`).
            sx={{ color: (t) => (t.palette.mode === 'dark' ? '#868dfb' : '#4d55d6') }}
            onClick={(e) => e.stopPropagation()}
          >
            {h.detail}
          </Link>
        ) : (
          h.detail || (h.blog ? '' : '—')
        )}
      </div>
    </>
  );
};

const PersonKnopf: FC<{ r: PlxOrderListRow; onPerson: (email: string) => void }> = ({
  r,
  onPerson,
}) =>
  r.email ? (
    <button
      type="button"
      className="person"
      title="Alle Bestellungen dieser Adresse (alle Zeiträume)"
      aria-label="Alle Bestellungen dieser Adresse"
      onClick={(e) => {
        e.stopPropagation();
        onPerson(r.email as string);
      }}
    >
      <PersonSearchIcon sx={{ fontSize: 15 }} />
    </button>
  ) : null;

/** Enter/Leertaste öffnet die Bestellung — nur auf der Zeile selbst, Links und Knöpfe darin behalten ihr Verhalten. */
const beiTaste = (onOpen: (nr: string) => void, nr: string) => (e: KeyboardEvent<HTMLElement>) => {
  if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
  e.preventDefault();
  onOpen(nr);
};

const TabellenZeile: FC<ZeileProps> = memo(({ r, onOpen, onPerson, netto }) => {
  const s = stand(r);
  const g = geld(r, netto);
  const name = kundeName(r);
  return (
    <tr
      tabIndex={0}
      aria-label={`Bestellung ${r.ordernumber} öffnen`}
      onClick={() => onOpen(r.ordernumber)}
      onKeyDown={beiTaste(onOpen, r.ordernumber)}
      className={r.vertrag_sts === 'V' ? 'gedimmt' : undefined}
    >
      <td>
        <div className="eins">{datumZeit(r.created_on)}</div>
        <div className="zwei">{r.ordernumber}</div>
      </td>
      <td>
        <div className="eins" title={r.product_name}>
          {r.product_name}
        </div>
        <div className="zwei">
          {[r.werbeaktion_wbkz, preisText(r)].filter((x) => x).join(' · ')}
        </div>
      </td>
      <td>
        <HerkunftZelle r={r} sektion={netto} />
      </td>
      <td>
        <div className="eins kunde" title={r.email ?? undefined}>
          <span className="mail">{r.email ?? (r.is_provisional ? 'Kundendaten folgen' : '—')}</span>
          <PersonKnopf r={r} onPerson={onPerson} />
        </div>
        <div className="zwei" title={name || undefined}>
          {name}
          {name && kundeMarken(r, netto).length > 0 ? ' ' : ''}
          <Marken r={r} sektion={netto} />
        </div>
      </td>
      <td className="umbruch">
        <div className="eins">
          <span className={`stand ${s.ton}`} title={s.tip}>
            {s.label}
          </span>
        </div>
        <div className="zwei" title={s.detail}>
          {s.detail}
        </div>
      </td>
      <td className="rechts umbruch">
        <div className="eins" title={g.tip}>
          {g.text}
        </div>
        <div className={`zwei${g.warn ? ' warn' : ''}`} title={g.detail}>
          {g.detail}
        </div>
      </td>
      {netto && (
        <td className="rechts">
          <div className="eins" title={TERMS.bezahltNettoListe.tip}>
            {nettoText(r)}
          </div>
          {nettoVorlaeufig(r) && (
            <div className="zwei" title={TERMS.vorlaeufig.tip}>
              {TERMS.vorlaeufig.short}
            </div>
          )}
        </td>
      )}
    </tr>
  );
});
TabellenZeile.displayName = 'TabellenZeile';

const KartenZeile: FC<ZeileProps> = memo(({ r, onOpen, onPerson, netto }) => {
  const s = stand(r);
  const g = geld(r, netto);
  const h = herkunft(r, netto);
  return (
    <div
      className="karte"
      role="button"
      tabIndex={0}
      aria-label={`Bestellung ${r.ordernumber} öffnen`}
      onClick={() => onOpen(r.ordernumber)}
      onKeyDown={beiTaste(onOpen, r.ordernumber)}
    >
      <div className="kopf">
        <span className="zwei">{datumZeit(r.created_on)}</span>
        <span className={`stand ${s.ton}`} title={s.tip}>
          {s.label}
        </span>
      </div>
      <div className="eins">{r.product_name}</div>
      <div className="zwei">{[preisText(r), s.detail].filter((x) => x).join(' · ')}</div>
      <div className="zwei">
        <strong>{h.kanal}</strong>
        {h.blog ? ` · ${h.blog}` : ''}
        {h.detail ? ` · ${h.detail}` : ''}
      </div>
      <div className="fuss">
        <span className="kunde">
          <span className="mail">{r.email ?? (r.is_provisional ? 'Kundendaten folgen' : '—')}</span>
          <PersonKnopf r={r} onPerson={onPerson} />
        </span>
        <span className="geld" title={netto && nettoVorlaeufig(r) ? TERMS.vorlaeufig.tip : undefined}>
          {netto
            ? `${nettoText(r)} netto${nettoVorlaeufig(r) ? ` (${TERMS.vorlaeufig.short})` : ''} · ${g.text} brutto`
            : g.text}
        </span>
      </div>
      {(kundeMarken(r, netto).length > 0 || g.detail) && (
        <div className="zwei">
          <Marken r={r} sektion={netto} />
          {g.detail && <span className="warn"> {g.detail}</span>}
        </div>
      )}
    </div>
  );
});
KartenZeile.displayName = 'KartenZeile';

interface Props {
  onOpenOrder: (ordernumber: string) => void;
  /** Anzahl der geladenen Bestellungen des Zeitraums — für den Sprung-Chip im Seitenkopf. */
  onCount?: (n: number | null) => void;
  /**
   * Eigener Filter statt des globalen FilterContext (Sektion /auswertungen/bestellungen: nur Zeitraum, ohne die
   * Seitenfilter der alten /plx-orders). Muss stabil sein (useMemo) — ein neues Objekt je Render lädt neu.
   */
  filter?: IFilterState;
  /**
   * Netto-Spalte „Bisher bezahlt (netto)", Netto-Summe im Kopf und im Excel (P5) — und Sektions-Modus: Kanal, Blog und
   * Kundentypen heißen wie in den Übersichten der Seite, Brutto ist beschriftet, leere Beträge „—" (P5-Review).
   */
  netto?: boolean;
  /**
   * Suchbegriff von außen (URL `?suche=` der Seite /auswertungen/bestellungen). Ändert er sich, übernimmt das Feld ihn;
   * jede Eingabe meldet `onSucheChange`, damit die Seite sie in der URL hält.
   */
  suche?: string;
  onSucheChange?: (suche: string) => void;
  /** Schnellfilter von außen (URL `?liste=verweigert` der Seite /auswertungen/bestellungen); unbekannte Schlüssel → „Alle". */
  startFilter?: string;
  /** Jeder Wechsel des Schnellfilters (Seite hält ihn in der URL, `?liste=`). */
  onFilterChange?: (key: string) => void;
}

const OrderList: FC<Props> = ({
  onOpenOrder,
  onCount,
  filter,
  netto = false,
  suche: sucheVonAussen,
  onSucheChange,
  startFilter,
  onFilterChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // Karten statt Tabelle nach der Breite der LISTE, nicht des Fensters: bei 1.024 px mit offener Seitenleiste blieben der
  // Tabelle 660 px, und die Hälfte der Zellen war abgeschnitten (Bedienprüfung 28.10.).
  const fensterSchmal = useMediaQuery(theme.breakpoints.down('md'));
  const [breiteRef, breitePx] = useBreitePx();
  const mobil = fensterSchmal || (breitePx !== null && breitePx < KARTEN_UNTER_PX);
  const sucheRef = useRef<HTMLInputElement>(null);
  const kontext = useContext(FilterContext)!;
  const filterState = filter ?? kontext.filterState;
  const spalten = netto ? SPALTEN_NETTO : SPALTEN;

  const [rows, setRows] = useState<PlxOrderListRow[] | null>(null);
  const [meta, setMeta] = useState<PlxOrderListMeta | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  /** Gesetzt = Such-Modus über alle Zeiträume mit diesem Begriff. */
  const [globaleSuche, setGlobaleSuche] = useState<string | null>(null);

  const [eingabe, setEingabeLokal] = useState(sucheVonAussen ?? '');
  const suche = useDeferredValue(eingabe);
  const setEingabe = (v: string) => {
    setEingabeLokal(v);
    onSucheChange?.(v);
  };
  // Neuer Begriff von außen (Link mit ?suche=, Zurück im Browser): ins Feld übernehmen.
  useEffect(() => {
    if (sucheVonAussen !== undefined) setEingabeLokal((alt) => (alt === sucheVonAussen ? alt : sucheVonAussen));
  }, [sucheVonAussen]);
  const [filterKey, setFilterKeyLokal] = useState('alle');
  const setFilterKey = (k: string) => {
    setFilterKeyLokal(k);
    onFilterChange?.(k);
  };
  // Gesteuert (Seite hält den Filter in der URL): fehlt `?liste=` oder ist er unbekannt, gilt „Alle" — sonst blieb nach
  // „Zurück" z. B. „Verweigert" aktiv, obwohl die URL ihn nicht mehr trägt (Critical Review 28.10.).
  const gesteuert = onFilterChange !== undefined;
  useEffect(() => {
    const gueltig = Boolean(startFilter && SCHNELLFILTER.some((f) => f.key === startFilter));
    if (gueltig) setFilterKeyLokal(startFilter as string);
    else if (gesteuert) setFilterKeyLokal('alle');
  }, [startFilter, gesteuert]);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'bestellt',
    dir: 'desc',
  });
  const [seite, setSeite] = useState(0);
  const [zeilen, setZeilen] = useState<number>(ladeZeilen);
  const listeRef = useRef<HTMLDivElement>(null);
  /** Eigener Scrollbereich der Tabelle (fester Kopf) — sein scrollTop überlebt sonst jeden Seitenwechsel. */
  const tabelleRef = useRef<HTMLDivElement>(null);

  // Laden mit Abbruch-Flag: bei schnellen Filterwechseln gewinnt sonst das zuletzt EINTREFFENDE Ergebnis.
  useEffect(() => {
    let abgebrochen = false;
    setLaedt(true);
    setFehler(null);
    fetchPlxOrderList(filterState, globaleSuche ?? undefined)
      .then((res) => {
        if (abgebrochen) return;
        setRows(res.rows);
        setMeta(res.meta);
      })
      .catch((e: unknown) => {
        if (abgebrochen) return;
        setFehler(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!abgebrochen) setLaedt(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, [filterState, globaleSuche]);

  useEffect(() => {
    if (!onCount) return;
    onCount(rows && meta?.mode === 'zeitraum' ? rows.length : null);
  }, [rows, meta, onCount]);

  const mitText = useMemo(() => (rows ?? []).map((r) => ({ r, t: suchtext(r, netto) })), [rows, netto]);

  // Im Such-Modus hat der Server schon gesucht — das Feld filtert dann nicht noch einmal lokal.
  const lokaleSuche = globaleSuche ? '' : suche.trim();
  // Sektion: ein Kanal- oder Blog-Name der Übersichten filtert genau EIN Feld (`namensTreffer`).
  const treffer = useMemo(
    () => (netto && lokaleSuche ? namensTreffer(rows ?? [], lokaleSuche) : null),
    [netto, lokaleSuche, rows],
  );
  const gesucht = useMemo(() => {
    if (!lokaleSuche) return mitText.map((x) => x.r);
    if (treffer) return mitText.filter((x) => treffer.test(x.r)).map((x) => x.r);
    return mitText.filter((x) => passtSuche(x.t, lokaleSuche)).map((x) => x.r);
  }, [mitText, lokaleSuche, treffer]);
  const zaehler = useMemo(
    () => Object.fromEntries(SCHNELLFILTER.map((f) => [f.key, gesucht.filter(f.test).length])),
    [gesucht],
  );
  const aktiverFilter = SCHNELLFILTER.find((f) => f.key === filterKey) ?? SCHNELLFILTER[0];
  const gefiltert = useMemo(() => gesucht.filter(aktiverFilter.test), [gesucht, aktiverFilter]);
  const sortiert = useMemo(() => sortiere(gefiltert, sort.key, sort.dir, netto), [gefiltert, sort, netto]);
  const summe = useMemo(() => summen(gefiltert), [gefiltert]);
  const sichtbar = useMemo(
    () => (zeilen === -1 ? sortiert : sortiert.slice(seite * zeilen, seite * zeilen + zeilen)),
    [sortiert, seite, zeilen],
  );

  // Jede Änderung der Auswahl springt auf Seite 1 — sonst steht man nach einer Suche auf einer leeren Seite.
  useEffect(() => {
    setSeite(0);
  }, [rows, lokaleSuche, filterKey, sort, zeilen]);

  // Neue Seite oder neue Auswahl beginnt bei der ersten Zeile, nicht dort, wo die vorige gescrollt war.
  useEffect(() => {
    if (tabelleRef.current) tabelleRef.current.scrollTop = 0;
  }, [seite, rows, lokaleSuche, filterKey, sort, zeilen]);

  const sortiereNach = (key: SortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: key === 'bestellt' || key === 'umsatz' || key === 'netto' ? 'desc' : 'asc' },
    );

  const sucheGlobal = (begriff: string) => {
    setFilterKey('alle');
    // Nur ins Feld, nicht in die URL: das Personen-Symbol sucht nach einer E-Mail-Adresse, die sonst im Verlauf und in
    // geteilten Links stünde (Bedienprüfung 28.10.).
    setEingabeLokal(begriff);
    setGlobaleSuche(begriff.trim());
    listeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const zurueckZumZeitraum = () => {
    setGlobaleSuche(null);
    setEingabe('');
  };

  const exportieren = () => {
    const name =
      meta?.mode === 'suche'
        ? `bestellungen_suche_${(meta.q ?? '').replace(/[^\w.@-]+/g, '_').slice(0, 40)}`
        : `bestellungen_${ymd(filterState.startDate)}_${ymd(filterState.endDate)}`;
    exportToExcel(
      sortiert.map((r) => exportZeile(r, netto)),
      name,
    );
  };

  const suchKurz = eingabe.trim().length > 0 && eingabe.trim().length < 3;
  const onPerson = sucheGlobal;

  return (
    <Box
      ref={listeRef}
      id={ORDER_LIST_ANCHOR}
      sx={{
        scrollMarginTop: 16,
        backgroundColor: colors.primary[400],
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        p: { xs: 1.5, md: 2 },
      }}
    >
      {/* Kopf: Titel, Summen der sichtbaren Auswahl, Rücksprung */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
          Bestellungen
        </Typography>
        {rows && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatNumber(summe.bestellungen, 0)}{' '}
            {summe.bestellungen === 1 ? 'Bestellung' : 'Bestellungen'} · davon{' '}
            {formatNumber(summe.anfos, 0)} {summe.anfos === 1 ? 'Anfo' : 'Anfos'} ·{' '}
            <Tooltip title={netto ? TERMS.umsatzBruttoListe.tip : TERMS.umsatz.tip} arrow>
              <span>
                {netto ? TERMS.umsatzBruttoListe.label : TERMS.umsatz.label}: <strong>{formatEur(summe.umsatz)}</strong>
              </span>
            </Tooltip>
            {netto && (
              <>
                {' · '}
                <Tooltip title={TERMS.bezahltNettoListeSumme.tip} arrow>
                  <span>
                    {TERMS.bezahltNettoListeSumme.label}: <strong>{formatEur(summe.umsatzNetto)}</strong>
                  </span>
                </Tooltip>
              </>
            )}
            {(lokaleSuche || filterKey !== 'alle') && rows.length !== gefiltert.length
              ? ` (Auswahl von ${formatNumber(rows.length, 0)})`
              : ''}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          startIcon={<ArrowUpwardIcon />}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{ color: 'text.secondary' }}
        >
          nach oben
        </Button>
      </Box>

      {/* Werkzeugleiste */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <TextField
          size="small"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              eingabe.trim().length >= 3 &&
              !globaleSuche &&
              gesucht.length === 0
            ) {
              sucheGlobal(eingabe);
            }
          }}
          disabled={Boolean(globaleSuche)}
          placeholder="Suchen: E-Mail, Name, Bestell- oder Kundennummer, Produkt, Webinar, Kampagne"
          inputProps={{ 'aria-label': 'Bestellungen durchsuchen' }}
          inputRef={sucheRef}
          sx={{ flex: { xs: '1 1 100%', md: '1 1 420px' }, maxWidth: { md: 560 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment:
              eingabe && !globaleSuche ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Suche leeren"
                    onClick={() => {
                      setEingabe('');
                      sucheRef.current?.focus();
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          }}
        />
        {!globaleSuche && eingabe.trim().length >= 3 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => sucheGlobal(eingabe)}
          >
            In allen Zeiträumen suchen
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportieren}
          disabled={!rows || sortiert.length === 0}
          title="Die angezeigte Auswahl (Suche, Schnellfilter, Sortierung) mit deutschen Spaltennamen — mit E-Mail und Name, ohne Anschrift"
        >
          Excel ({formatNumber(sortiert.length, 0)})
        </Button>
      </Box>

      {treffer?.mehrdeutig && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          „{treffer.name}“ gibt es als Kanal und als Blog — gefiltert ist der Kanal. Für den Blog „Blog: {treffer.name}“
          eingeben.
        </Typography>
      )}
      {suchKurz && !globaleSuche && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Für die Suche über alle Zeiträume mindestens 3 Zeichen.
        </Typography>
      )}
      {lokaleSuche.length >= 3 && !globaleSuche && rows && gesucht.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Im gewählten Zeitraum keine Bestellung zu „{lokaleSuche}“.{' '}
          <Link
            component="button"
            onClick={() => sucheGlobal(eingabe)}
            sx={{ verticalAlign: 'baseline' }}
          >
            In allen Zeiträumen suchen
          </Link>
        </Alert>
      )}
      {/* Der Ausweg steht IMMER da, sobald gesucht wird — auch beim Laden und nach einem Fehler; das
          Suchfeld ist in diesem Modus gesperrt (Codex-Befund 20.10.). */}
      {globaleSuche && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={zurueckZumZeitraum}>
              Zurück zum Zeitraum
            </Button>
          }
        >
          {meta?.mode === 'suche' && meta.q === globaleSuche && !laedt ? (
            <>
              Suche über alle Zeiträume nach „{meta.q}“ — {formatNumber(rows?.length ?? 0, 0)}{' '}
              Treffer
              {meta.truncated ? ` (nur die neuesten ${meta.limit}; bitte genauer suchen)` : ''}.{' '}
              {netto
                ? 'Der Zeitraum oben gilt hier nicht; Testbestellungen bleiben ausgeblendet.'
                : 'Die Filter oben gelten hier nicht, nur „Test-Orders ausblenden“.'}
            </>
          ) : fehler ? (
            <>Suche über alle Zeiträume nach „{globaleSuche}“ fehlgeschlagen.</>
          ) : (
            <>Suche über alle Zeiträume nach „{globaleSuche}“ läuft …</>
          )}
        </Alert>
      )}

      {/* Schnellfilter */}
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}
        role="group"
        aria-label="Schnellfilter"
      >
        {SCHNELLFILTER.filter(
          (f) => f.key === 'alle' || (zaehler[f.key] ?? 0) > 0 || f.key === filterKey,
        ).map((f) => {
          const aktiv = f.key === filterKey;
          return (
            // `describeChild`: der Tooltip beschreibt, der Name bleibt der Knopftext („Testphase 75").
            <Tooltip key={f.key} title={f.tip} arrow describeChild>
              <Box
                component="button"
                type="button"
                aria-pressed={aktiv}
                onClick={() => setFilterKey(f.key)}
                sx={{
                  font: 'inherit',
                  fontSize: 12.5,
                  cursor: 'pointer',
                  borderRadius: '14px',
                  px: 1.25,
                  py: 0.4,
                  border: '1px solid',
                  borderColor: aktiv ? '#4cceac' : 'divider',
                  backgroundColor: aktiv ? 'rgba(76,206,172,0.16)' : 'transparent',
                  color: 'text.primary',
                  fontWeight: aktiv ? 600 : 400,
                  '&:hover': { borderColor: '#4cceac' },
                }}
              >
                {f.label}{' '}
                <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(zaehler[f.key] ?? 0, 0)}
                </span>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {fehler && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Bestellungen konnten nicht geladen werden: {fehler}
        </Alert>
      )}

      <Box
        ref={breiteRef}
        sx={{
          position: 'relative',
          opacity: laedt && rows ? 0.55 : 1,
          transition: 'opacity .15s',
          // ── gemeinsame Stile für Tabelle UND Karten (eine Klasse statt Emotion je Zelle) ──
          '& .eins': {
            fontSize: 13,
            lineHeight: 1.35,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .zwei': {
            fontSize: 12,
            lineHeight: 1.35,
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .zwei.warn, & .warn': { color: theme.palette.mode === 'dark' ? '#ffb74d' : '#c25e00' },
          '& .kunde': { display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 },
          '& .kunde .mail': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          },
          '& .person': {
            flex: '0 0 auto',
            border: 0,
            background: 'transparent',
            color: 'text.secondary',
            cursor: 'pointer',
            p: '1px',
            lineHeight: 0,
            borderRadius: '4px',
            '&:hover, &:focus-visible': { color: 'text.primary', backgroundColor: 'action.hover' },
          },
          '& .stand': {
            display: 'inline-block',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '18px',
            px: '8px',
            borderRadius: '9px',
            border: '1px solid',
            color: 'text.primary',
            whiteSpace: 'nowrap',
          },
          '& .stand.test': { borderColor: '#6870fa', backgroundColor: 'rgba(104,112,250,0.14)' },
          '& .stand.bezahlt': { borderColor: '#4cceac', backgroundColor: 'rgba(76,206,172,0.20)' },
          '& .stand.offen': { borderColor: '#f57c00', backgroundColor: 'rgba(245,124,0,0.14)' },
          '& .stand.abgelehnt': {
            borderColor: 'divider',
            color: 'text.secondary',
            fontWeight: 400,
          },
          '& .stand.vorlaeufig': {
            borderColor: 'text.disabled',
            borderStyle: 'dashed',
            color: 'text.secondary',
          },
          '& .stand.neutral': { borderColor: 'divider' },
          '& .marke': {
            display: 'inline-block',
            fontSize: 11,
            lineHeight: '15px',
            px: '5px',
            mr: '4px',
            borderRadius: '3px',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
          },
          '& .marke.gut': { borderColor: '#4cceac' },
          '& .marke.warn': { borderColor: '#f57c00' },
          '& .marke.schlecht': { borderColor: '#d32f2f', color: 'text.primary', fontWeight: 600 },
        }}
      >
        {!rows && laedt && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Bestellungen werden geladen …
          </Typography>
        )}
        {rows && sortiert.length === 0 && !laedt && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Keine Bestellung für diese Auswahl.
          </Typography>
        )}

        {rows && sortiert.length > 0 && !mobil && (
          <Box
            ref={tabelleRef}
            sx={{
              maxHeight: 'calc(100vh - 140px)',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '4px',
              '& table': {
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                borderSpacing: 0,
              },
              '& th': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: colors.primary[400],
                textAlign: 'left',
                fontSize: 12.5,
                fontWeight: 600,
                color: 'text.primary',
                px: 1.25,
                py: 1,
                borderBottom: '2px solid',
                borderColor: 'divider',
                whiteSpace: 'nowrap',
                userSelect: 'none',
              },
              '& th button': {
                font: 'inherit',
                color: 'inherit',
                background: 'none',
                border: 0,
                p: 0,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
              },
              '& th.rechts, & td.rechts': { textAlign: 'right' },
              '& th.rechts button': { flexDirection: 'row-reverse' },
              '& td': {
                px: 1.25,
                py: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                verticalAlign: 'top',
                overflow: 'hidden',
                fontVariantNumeric: 'tabular-nums',
              },
              '& td.rechts .eins, & td.rechts .zwei': { textAlign: 'right' },
              // Hinweise zu Geld und Zahlstand duerfen umbrechen statt im Tooltip zu verschwinden.
              '& td.umbruch .zwei': { whiteSpace: 'normal' },
              '& tbody tr': { cursor: 'pointer' },
              '& tbody tr:hover td, & tbody tr:focus-visible td': {
                backgroundColor: 'action.hover',
              },
              '& tbody tr:focus-visible': { outline: '2px solid #6870fa', outlineOffset: '-2px' },
              '& tbody tr.gedimmt td .eins': { color: 'text.secondary' },
            }}
          >
            <table>
              <colgroup>
                {spalten.map((c) => (
                  <col key={c.key} style={{ width: c.breite }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {spalten.map((c) => {
                    const aktiv = sort.key === c.key;
                    return (
                      <th
                        key={c.key}
                        className={c.align === 'right' ? 'rechts' : undefined}
                        aria-sort={
                          aktiv ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                        }
                      >
                        <button type="button" onClick={() => sortiereNach(c.key)} title={c.tip}>
                          {c.label}
                          <span aria-hidden style={{ opacity: aktiv ? 1 : 0.25, fontSize: 11 }}>
                            {aktiv && sort.dir === 'asc' ? '▲' : '▼'}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sichtbar.map((r) => (
                  <TabellenZeile
                    key={r.ordernumber}
                    r={r}
                    onOpen={onOpenOrder}
                    onPerson={onPerson}
                    netto={netto}
                  />
                ))}
              </tbody>
            </table>
          </Box>
        )}

        {rows && sortiert.length > 0 && mobil && (
          // Karten haben keine Spaltenköpfe — die Sortierung als Auswahl darüber (Bedienprüfung 28.10.).
          <TextField
            select
            size="small"
            label="Sortieren"
            value={`${sort.key}:${sort.dir}`}
            onChange={(e) => {
              const [key, dir] = e.target.value.split(':') as [SortKey, 'asc' | 'desc'];
              setSort({ key, dir });
            }}
            sx={{ mb: 1, minWidth: 240 }}
          >
            {kartenSortierung(netto).map((o) => (
              <MenuItem key={o.wert} value={o.wert}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {rows && sortiert.length > 0 && mobil && (
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              '& .karte': {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                p: 1.25,
                cursor: 'pointer',
                minWidth: 0,
                '&:hover, &:focus-visible': { backgroundColor: 'action.hover' },
                '&:focus-visible': { outline: '2px solid #6870fa', outlineOffset: '1px' },
              },
              '& .karte .kopf, & .karte .fuss': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              },
              '& .karte .eins': { fontWeight: 600, mt: 0.5 },
              '& .karte .fuss': { mt: 0.5, fontSize: 13 },
              '& .karte .geld': {
                fontWeight: 600,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              },
            }}
          >
            {sichtbar.map((r) => (
              <KartenZeile
                key={r.ordernumber}
                r={r}
                onOpen={onOpenOrder}
                onPerson={onPerson}
                netto={netto}
              />
            ))}
          </Box>
        )}
      </Box>

      {rows && sortiert.length > 0 && (
        <TablePagination
          component="div"
          count={sortiert.length}
          page={
            zeilen === -1
              ? 0
              : Math.min(seite, Math.max(0, Math.ceil(sortiert.length / zeilen) - 1))
          }
          onPageChange={(_e, p) => {
            setSeite(p);
            listeRef.current?.scrollIntoView({ block: 'start' });
          }}
          rowsPerPage={zeilen}
          rowsPerPageOptions={ZEILEN_OPTIONEN}
          onRowsPerPageChange={(e) => {
            const v = parseInt(e.target.value, 10);
            setZeilen(v);
            merkeZeilen(v);
          }}
          labelRowsPerPage="Zeilen pro Seite"
          labelDisplayedRows={({ from, to, count }) =>
            `${formatNumber(from, 0)}–${formatNumber(to, 0)} von ${formatNumber(count, 0)}`
          }
          getItemAriaLabel={(type) =>
            type === 'next'
              ? 'Nächste Seite'
              : type === 'previous'
                ? 'Vorige Seite'
                : type === 'first'
                  ? 'Erste Seite'
                  : 'Letzte Seite'
          }
          showFirstButton={!mobil}
          showLastButton={!mobil}
          // MUIs Leiste bricht nicht um — bei 390 px lagen die Blätter-Knöpfe rechts außerhalb (gemessen 20.10.).
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              pl: { xs: 0, md: 2 },
            },
            '& .MuiTablePagination-spacer': { display: { xs: 'none', md: 'block' } },
            '& .MuiTablePagination-actions': { ml: { xs: 1, md: 2.5 } },
          }}
        />
      )}
    </Box>
  );
};

export default OrderList;
